import { type RefObject, useCallback, useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/shallow';

import { cloneArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';
import type { ArticleMesh } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleMesh';
import type { ArticleReference } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleReference';

interface BatchedMeshLifecycleHandlerProps {
    articleId: string;
    articleMeshes: ArticleMesh[];
    isMainCanvas: boolean;
    articleRef: RefObject<ArticleReference>;
    pendingColliderIdsRef: RefObject<Set<string> | null>;
}

/** Resolved batch operations for the current canvas (main or navigator). */
interface ResolvedBatchOps {
    registerGeometry: (articleId: string, meshes: ArticleMesh[]) => void;
    addInstances: (articleId: string, ids: string[]) => void;
    removeInstances: (ids: string[]) => void;
    isBatchActive: () => boolean;
}

/**
 * Selection replaces the live drag reference of an article with a cloned snapshot. Without syncing, instances
 * added afterwards (e.g. by automated placement solvers) are rendered but can't be resolved from batched mesh
 * hits until the live reference gets re-registered (e.g. by box selection).
 */
function syncRegisteredDragReference(
    articleId: string,
    liveArticleRef: RefObject<ArticleReference>,
    newInstances: Record<string, ArticleInstance>,
    prevInstances: Record<string, ArticleInstance> | undefined
) {
    const state = usePlacementStore.getState();
    const registeredRef = state.articleDragRefsMap.current.get(articleId)?.articleRef;
    const snapshot = registeredRef?.current;
    if (!registeredRef || !snapshot || registeredRef === liveArticleRef) {
        return;
    }

    // keep in-progress drag state of the snapshot, otherwise adopt store changes
    const keepSnapshotState = state.isDraggingActive() || state.isAutoDragActiveRef.current;
    const snapshotInstances = snapshot.articleInstances;
    const syncedInstances: Record<string, ArticleInstance> = {};
    let hasChanges = false;
    let syncedCount = 0;

    for (const instanceId in newInstances) {
        const snapshotInstance = snapshotInstances[instanceId];
        const isStoreInstanceChanged = newInstances[instanceId] !== prevInstances?.[instanceId];
        if (snapshotInstance && (keepSnapshotState || !isStoreInstanceChanged)) {
            syncedInstances[instanceId] = snapshotInstance;
        } else {
            syncedInstances[instanceId] = cloneArticleInstance(newInstances[instanceId]);
            hasChanges = true;
        }
        syncedCount++;
    }

    if (!hasChanges && syncedCount === Object.keys(snapshotInstances).length) {
        return;
    }

    registeredRef.current = { ...snapshot, articleInstances: syncedInstances };
}

/**
 * Manages the lifecycle of batched mesh instances and geometry registration for articles.
 */
export const useBatchedMeshLifecycleHandler = ({
    articleId,
    articleMeshes,
    isMainCanvas,
    articleRef,
    pendingColliderIdsRef
}: BatchedMeshLifecycleHandlerProps) => {
    const updateInstanceMatrices = usePlacementStore(useShallow((state) => state.updateInstanceMatrices));

    const initializeBatchInstances = useCallback(
        (instanceIds: string[], shouldUpdateColliders: boolean) => {
            if (instanceIds.length === 0) {
                return;
            }

            if (isMainCanvas && shouldUpdateColliders) {
                if (pendingColliderIdsRef.current === null) {
                    pendingColliderIdsRef.current = new Set<string>();
                }
                for (const id of instanceIds) {
                    pendingColliderIdsRef.current.add(id);
                }
            }

            updateInstanceMatrices(true, articleRef, shouldUpdateColliders, instanceIds);
        },
        [articleRef, isMainCanvas, pendingColliderIdsRef, updateInstanceMatrices]
    );

    // Resolve once which set of batch operations to use based on canvas type
    const batchOps: ResolvedBatchOps = useMemo(() => {
        if (isMainCanvas) {
            return {
                registerGeometry: usePlacementStore.getState().registerArticleGeometry,
                addInstances: usePlacementStore.getState().addBatchInstances,
                removeInstances: usePlacementStore.getState().removeBatchInstances,
                isBatchActive: usePlacementStore.getState().isBatchModeActive
            };
        }
        return {
            registerGeometry: usePlacementStore.getState().registerNavigatorArticleGeometry,
            addInstances: usePlacementStore.getState().addNavigatorBatchInstances,
            removeInstances: usePlacementStore.getState().removeNavigatorBatchInstances,
            isBatchActive: usePlacementStore.getState().isNavigatorBatchModeActive
        };
    }, [isMainCanvas]);

    // Initial registration of instances
    useEffect(() => {
        batchOps.registerGeometry(articleId, articleMeshes);

        const instanceIds = Object.keys(articleRef.current.articleInstances);
        batchOps.addInstances(articleId, instanceIds);
        if (instanceIds.length > 0) {
            initializeBatchInstances(instanceIds, isMainCanvas);
        }

        return () => {
            const instances = Object.keys(articleRef.current.articleInstances);
            if (batchOps.isBatchActive()) {
                batchOps.removeInstances(instances);
            }
        };
    }, [articleId, articleMeshes, isMainCanvas, articleRef, batchOps, initializeBatchInstances]);

    // keeps geometry slots and batch instances in sync with the Zustand articles store.
    useEffect(() => {
        const subscribedArticle = usePlacementStore.subscribe(
            (state) => state.articles[articleId],
            (newArticle, prevArticle) => {
                if (!newArticle) {
                    return;
                }

                const prevInstances = prevArticle?.articleInstances;
                const newInstances = newArticle.articleInstances;
                const currentArticleInstances = articleRef.current.articleInstances;
                const newArticleInstances: Record<string, ArticleInstance> = {};

                for (const instanceId in newInstances) {
                    const instance = newInstances[instanceId];
                    newArticleInstances[instanceId] =
                        instance !== prevInstances?.[instanceId]
                            ? cloneArticleInstance(instance)
                            : (currentArticleInstances[instanceId] ?? cloneArticleInstance(instance));
                }

                articleRef.current = {
                    ...articleRef.current,
                    articleInstances: newArticleInstances
                };

                if (isMainCanvas && newInstances !== prevInstances) {
                    syncRegisteredDragReference(articleId, articleRef, newInstances, prevInstances);
                }

                if (!batchOps.isBatchActive()) {
                    return;
                }

                batchOps.registerGeometry(articleId, articleMeshes);

                const addedIds: string[] = [];
                for (const instanceId in newInstances) {
                    if (!prevInstances || !(instanceId in prevInstances)) {
                        addedIds.push(instanceId);
                    }
                }

                const removedIds: string[] = [];
                if (prevInstances) {
                    for (const instanceId in prevInstances) {
                        if (!(instanceId in newInstances)) {
                            removedIds.push(instanceId);
                        }
                    }
                }

                if (addedIds.length > 0) {
                    batchOps.addInstances(articleId, addedIds);
                }
                if (removedIds.length > 0) {
                    batchOps.removeInstances(removedIds);
                }

                if (addedIds.length > 0) {
                    initializeBatchInstances(addedIds, isMainCanvas);
                }
            }
        );

        return () => {
            subscribedArticle();
        };
    }, [articleId, isMainCanvas, articleMeshes, batchOps, articleRef, initializeBatchInstances]);
};
